/**
 * custom-payment-upload.js
 *
 * Handles drag-and-drop, file validation, image preview,
 * Formspree AJAX submission, and success/error UI states
 * for the Custom Payment Upload section.
 *
 * No external dependencies — pure vanilla JS.
 */

class CustomPaymentUpload extends HTMLElement {
  constructor() {
    super();
    /** @type {HTMLFormElement | null} */
    this._form = null;
    /** @type {HTMLElement | null} */
    this._uploadZone = null;
    /** @type {HTMLInputElement | null} */
    this._uploadInput = null;
    /** @type {HTMLElement | null} */
    this._filePreview = null;
    /** @type {HTMLImageElement | null} */
    this._fileThumb = null;
    /** @type {HTMLElement | null} */
    this._fileName = null;
    /** @type {HTMLElement | null} */
    this._fileSize = null;
    /** @type {HTMLElement | null} */
    this._fileRemove = null;
    /** @type {HTMLButtonElement | null} */
    this._submitBtn = null;
    /** @type {HTMLElement | null} */
    this._successEl = null;
    /** @type {HTMLElement | null} */
    this._errorBanner = null;
    /** @type {HTMLElement | null} */
    this._errorMsg = null;
    
    this._maxSizeMB = 10;
    this._formspreeId = '';
    this._imgbbKey = '';
    this._successMsg = '';
    
    /** @type {File | null} */
    this._selectedFile = null;
  }

  connectedCallback() {
    this._form         = /** @type {HTMLFormElement} */ (this.querySelector('.cpu__form'));
    this._uploadZone   = /** @type {HTMLElement} */ (this.querySelector('.cpu__upload-zone'));
    this._uploadInput  = /** @type {HTMLInputElement} */ (this.querySelector('.cpu__upload-input'));
    this._filePreview  = /** @type {HTMLElement} */ (this.querySelector('.cpu__file-preview'));
    this._fileThumb    = /** @type {HTMLImageElement} */ (this.querySelector('.cpu__file-thumb'));
    this._fileName     = /** @type {HTMLElement} */ (this.querySelector('.cpu__file-name'));
    this._fileSize     = /** @type {HTMLElement} */ (this.querySelector('.cpu__file-size'));
    this._fileRemove   = /** @type {HTMLElement} */ (this.querySelector('.cpu__file-remove'));
    this._submitBtn    = /** @type {HTMLButtonElement} */ (this.querySelector('.cpu__submit-btn'));
    this._successEl    = /** @type {HTMLElement} */ (this.querySelector('.cpu__success'));
    this._errorBanner  = /** @type {HTMLElement} */ (this.querySelector('.cpu__error-banner'));
    this._errorMsg     = /** @type {HTMLElement} */ (this.querySelector('.cpu__error-text'));

    this._maxSizeMB    = parseFloat(this.dataset.maxSizeMb || '10');
    this._formspreeId  = this.dataset.formspreeId || '';
    this._imgbbKey     = this.dataset.imgbbKey || '';
    this._successMsg   = this.dataset.successMsg  || 'Thank you! We received your payment proof and will verify it shortly.';

    this._selectedFile = null;

    if (!this._form || !this._uploadZone || !this._uploadInput) return;

    this._bindEvents();
  }

  /* -------- Bind all events -------- */
  _bindEvents() {
    if (!this._uploadInput || !this._uploadZone || !this._form) return;

    // File input change
    this._uploadInput.addEventListener('change', (e) => {
      const target = /** @type {HTMLInputElement} */ (e.target);
      if (target && target.files && target.files.length > 0) {
        const file = target.files[0];
        if (file) this._handleFile(file);
      }
    });

    // Drag and drop
    this._uploadZone.addEventListener('dragenter', (e) => {
      e.preventDefault();
      if (this._uploadZone) {
        this._uploadZone.dataset.dragging = 'true';
      }
    });

    this._uploadZone.addEventListener('dragover', (e) => {
      e.preventDefault();
      if (this._uploadZone) {
        this._uploadZone.dataset.dragging = 'true';
      }
    });

    this._uploadZone.addEventListener('dragleave', (e) => {
      const ev = /** @type {MouseEvent} */ (e);
      const related = /** @type {Node} */ (ev.relatedTarget);
      if (this._uploadZone && !this._uploadZone.contains(related)) {
        this._uploadZone.dataset.dragging = 'false';
      }
    });

    this._uploadZone.addEventListener('drop', (e) => {
      e.preventDefault();
      if (this._uploadZone) {
        this._uploadZone.dataset.dragging = 'false';
      }
      const ev = /** @type {DragEvent} */ (e);
      const files = ev.dataTransfer && ev.dataTransfer.files;
      if (files && files.length > 0) {
        const file = files[0];
        if (file) this._handleFile(file);
      }
    });

    // Remove file
    if (this._fileRemove) {
      this._fileRemove.addEventListener('click', (e) => {
        e.stopPropagation();
        this._clearFile();
      });
    }

    // Form submit
    this._form.addEventListener('submit', (e) => {
      e.preventDefault();
      this._handleSubmit();
    });

    // Field validation on blur
    const requiredFields = this._form.querySelectorAll('[required]');
    requiredFields.forEach(field => {
      const inputField = /** @type {HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement} */ (/** @type {unknown} */ (field));
      inputField.addEventListener('blur', () => this._validateField(inputField));
      inputField.addEventListener('input', () => {
        const wrap = /** @type {HTMLElement | null} */ (inputField.closest('.cpu__field'));
        if (wrap && wrap.dataset.error === 'true') {
          this._validateField(inputField);
        }
      });
    });
  }

  /* -------- File handling -------- */
  /**
   * @param {File} file
   */
  _handleFile(file) {
    if (!this._uploadZone) return;
    
    // Validate type
    const allowedTypes = ['image/jpeg', 'image/png', 'image/webp', 'image/heic', 'application/pdf'];
    if (!allowedTypes.includes(file.type)) {
      const wrap = /** @type {HTMLElement | null} */ (this._uploadZone.closest('.cpu__field'));
      if (wrap) {
        this._showFieldError(
          wrap,
          'Please upload an image (JPG, PNG, WEBP, HEIC) or PDF.'
        );
      }
      return;
    }

    // Validate size
    const maxBytes = this._maxSizeMB * 1024 * 1024;
    if (file.size > maxBytes) {
      const wrap = /** @type {HTMLElement | null} */ (this._uploadZone.closest('.cpu__field'));
      if (wrap) {
        this._showFieldError(
          wrap,
          `File is too large. Maximum size is ${this._maxSizeMB}MB.`
        );
      }
      return;
    }

    this._selectedFile = file;
    const wrap = /** @type {HTMLElement | null} */ (this._uploadZone.closest('.cpu__field'));
    if (wrap) {
      this._clearFieldError(wrap);
    }
    this._uploadZone.dataset.hasFile = 'true';

    // Update file info
    if (this._fileName) this._fileName.textContent = file.name;
    if (this._fileSize) this._fileSize.textContent  = this._formatBytes(file.size);

    // Show image thumbnail if it's an image
    if (file.type.startsWith('image/') && this._fileThumb) {
      const reader = new FileReader();
      reader.onload = (ev) => {
        if (this._fileThumb && ev.target) {
          this._fileThumb.src = /** @type {string} */ (ev.target.result);
          this._fileThumb.style.display = 'block';
        }
      };
      reader.readAsDataURL(file);
    } else if (this._fileThumb) {
      // PDF icon fallback
      this._fileThumb.style.display = 'none';
    }
  }

  _clearFile() {
    this._selectedFile = null;
    if (this._uploadInput) this._uploadInput.value = '';
    if (this._uploadZone) this._uploadZone.dataset.hasFile = 'false';
    if (this._fileThumb) { this._fileThumb.src = ''; }
    if (this._fileName)  { this._fileName.textContent = ''; }
    if (this._fileSize)  { this._fileSize.textContent = ''; }
  }

  /**
   * @param {number} bytes
   */
  _formatBytes(bytes) {
    if (bytes < 1024)       return bytes + ' B';
    if (bytes < 1024*1024)  return (bytes / 1024).toFixed(1) + ' KB';
    return (bytes / (1024*1024)).toFixed(1) + ' MB';
  }

  /* -------- Validation -------- */
  /**
   * @param {HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement} field
   */
  _validateField(field) {
    const wrap = /** @type {HTMLElement | null} */ (field.closest('.cpu__field'));
    if (!wrap) return true;

    let valid = true;

    if (field.required && !field.value.trim()) {
      this._showFieldError(wrap, wrap.dataset.requiredMsg || 'This field is required.');
      field.setAttribute('aria-invalid', 'true');
      valid = false;
    } else {
      this._clearFieldError(wrap);
      field.setAttribute('aria-invalid', 'false');
    }

    return valid;
  }

  _validateForm() {
    if (!this._form) return false;
    
    let valid = true;
    const requiredFields = this._form.querySelectorAll('[required]');
    requiredFields.forEach(field => {
      const inputField = /** @type {HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement} */ (/** @type {unknown} */ (field));
      if (!this._validateField(inputField)) valid = false;
    });

    // Check file
    if (!this._selectedFile && this._uploadZone) {
      const uploadField = /** @type {HTMLElement | null} */ (this._uploadZone.closest('.cpu__field'));
      if (uploadField) {
        this._showFieldError(uploadField, 'Please upload a screenshot of your payment.');
      }
      valid = false;
    }

    return valid;
  }

  /**
   * @param {HTMLElement} wrap
   * @param {string} msg
   */
  _showFieldError(wrap, msg) {
    if (!wrap) return;
    wrap.dataset.error = 'true';
    const errEl = wrap.querySelector('.cpu__field-error');
    if (errEl) {
      const msgEl = errEl.querySelector('.cpu__field-error-msg');
      if (msgEl) msgEl.textContent = msg;
    }
  }

  /**
   * @param {HTMLElement} wrap
   */
  _clearFieldError(wrap) {
    if (!wrap) return;
    wrap.dataset.error = 'false';
  }

  /* -------- Form submission (Formspree AJAX) -------- */
  async _handleSubmit() {
    if (!this._form) return;
    
    // Hide previous error
    if (this._errorBanner) this._errorBanner.dataset.visible = 'false';

    if (!this._validateForm()) {
      // Scroll to first error
      const firstErr = this._form.querySelector('[data-error="true"]');
      if (firstErr) firstErr.scrollIntoView({ behavior: 'smooth', block: 'center' });
      return;
    }

    if (!this._formspreeId) {
      this._showError('Form endpoint is not configured. Please contact the store owner.');
      return;
    }

    // Set loading state
    if (this._submitBtn) {
      this._submitBtn.dataset.loading = 'true';
      this._submitBtn.disabled = true;
    }

    try {
      const formData = new FormData(this._form);

      // If ImgBB key is provided, upload image first
      if (this._imgbbKey && this._selectedFile) {
        const imgData = new FormData();
        imgData.append('image', this._selectedFile);
        
        const imgRes = await fetch(`https://api.imgbb.com/1/upload?key=${this._imgbbKey}`, {
          method: 'POST',
          body: imgData
        });
        
        const imgJson = await imgRes.json();
        if (imgRes.ok && imgJson.data && imgJson.data.url) {
          // Append the direct image URL as a text field for Formspree
          formData.append('payment_screenshot_url', imgJson.data.url);
        } else {
          throw new Error(imgJson.error?.message || 'Failed to upload image to hosting service.');
        }
      } else if (this._selectedFile) {
        // Fallback to direct upload if no ImgBB key (might fail on free Formspree)
        formData.set('payment_screenshot', this._selectedFile, this._selectedFile.name);
      }

      const endpoint = `https://formspree.io/f/${this._formspreeId}`;
      const res = await fetch(endpoint, {
        method:  'POST',
        body:    formData,
        headers: { 'Accept': 'application/json' }
      });

      const data = await res.json();

      if (res.ok && (data.ok || data.next)) {
        this._showSuccess();
      } else {
        const errMsg = (data.errors && data.errors[0] && data.errors[0].message) ||
                       'Something went wrong. Please try again or contact us.';
        this._showError(errMsg);
      }

    } catch (err) {
      this._showError('A network error occurred. Please check your connection and try again.');
    } finally {
      if (this._submitBtn) {
        this._submitBtn.dataset.loading = 'false';
        this._submitBtn.disabled = false;
      }
    }
  }

  /* -------- UI state changes -------- */
  _showSuccess() {
    // Animate form out
    if (this._form) {
      this._form.dataset.submitted = 'true';
    }

    if (this._successEl) {
      this._successEl.dataset.visible = 'true';

      // Update success message dynamically if needed
      const msgEl = this._successEl.querySelector('.cpu__success-message');
      if (msgEl && this._successMsg) {
        msgEl.textContent = this._successMsg;
      }
    }

    // Scroll to top of component
    this.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }

  /**
   * @param {string} message
   */
  _showError(message) {
    if (!this._errorBanner) return;
    const textEl = this._errorBanner.querySelector('.cpu__error-text');
    if (textEl) textEl.textContent = message;
    this._errorBanner.dataset.visible = 'true';
    this._errorBanner.scrollIntoView({ behavior: 'smooth', block: 'center' });
  }
}

// Register Web Component
if (!customElements.get('custom-payment-upload')) {
  customElements.define('custom-payment-upload', CustomPaymentUpload);
}
